import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import {
  materialRequisitions,
  type MaterialRequisitionListItem,
} from '@/api/materialRequisitions';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import ImportedBadge from '@/shared/components/ImportedBadge';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { useOrgNames, agOrgCol } from '@/shared/hooks/useOrgNames';

const PERMISSION = 'production.material_transfer.manage';

export default function MaterialRequisitionsPage() {
  const navigate = useNavigate();
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const f = useDocListFilters();
  const orgNames = useOrgNames();

  // Серверна пагінація (Норма #29) — за зразком ProductionReportsPage.
  const { datasource, total } = useInfiniteGridDatasource<MaterialRequisitionListItem>(
    (limit, offset, sort) => materialRequisitions.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [...f.queryKey],
    { attachmentsType: 'doc.material_requisitions' },
  );

  const cols = useMemo<ColDef<MaterialRequisitionListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: MaterialRequisitionListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: 'Номер', width: 150, sortable: true },
    {
      field: 'document_date', headerName: 'Дата', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'total_cost', headerName: 'Вартість матеріалів', width: 180, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null
        ? Number(p.value).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [orgNames]);

  const canCreate = f.orgId ? allowedOrgIds.has(f.orgId) : perms.availableOrgs.length > 0;
  const tooltipNew = !canCreate
      ? `Немає прав створювати Вимогу-накладну у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Вимога-накладна (списання у виробництво)<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/material-requisitions/new')}
        >
          + Нова вимога
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} />
      </DocListFiltersRow>
      <InfiniteDocGrid<MaterialRequisitionListItem>
        deleteRow={{ remove: (id) => materialRequisitions.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="material-requisitions"
        onRowDoubleClicked={(r) => navigate(`/material-requisitions/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
